import * as React from 'react';
import { Dialog as DialogPrimitive } from '@base-ui/react/dialog';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { XIcon } from 'lucide-react';

function Dialog({ ...props }: DialogPrimitive.Root.Props) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}

function DialogTrigger({ ...props }: DialogPrimitive.Trigger.Props) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

const DialogPortal = React.forwardRef<
  HTMLDivElement,
  DialogPrimitive.Portal.Props
>(function DialogPortal(props, ref) {
  return (
    <DialogPrimitive.Portal data-slot="dialog-portal" ref={ref} {...props} />
  );
});

/**
 * The open dialog's portal node. A dialog rendered inside another dialog
 * portals here, so the parent focus manager still treats it as inside.
 */
const DialogPortalContainerContext = React.createContext<HTMLElement | null>(
  null
);

function DialogClose({ ...props }: DialogPrimitive.Close.Props) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

function DialogOverlay({
  className,
  ...props
}: DialogPrimitive.Backdrop.Props) {
  return (
    <DialogPrimitive.Backdrop
      data-slot="dialog-overlay"
      // Nested dialogs (e.g. Add Task over Edit Event) skip the backdrop
      // unless this is forced — otherwise the parent form stays sharp.
      forceRender
      className={cn(
        'tw:fixed tw:inset-0 tw:isolate tw:z-50 tw:bg-black/50 tw:duration-100 tw:supports-backdrop-filter:backdrop-blur-md tw:data-open:animate-in tw:data-open:fade-in-0 tw:data-closed:animate-out tw:data-closed:fade-out-0',
        className
      )}
      {...props}
    />
  );
}

function DialogContent({
  className,
  overlayClassName,
  children,
  showCloseButton = true,
  container,
  ...props
}: DialogPrimitive.Popup.Props & {
  showCloseButton?: boolean;
  overlayClassName?: string;
  /** Portal target. Nested dialogs default to the parent dialog's portal. */
  container?: DialogPrimitive.Portal.Props['container'];
}) {
  const parentPortal = React.useContext(DialogPortalContainerContext);
  const portalRef = React.useRef<HTMLDivElement>(null);
  const [portalNode, setPortalNode] = React.useState<HTMLElement | null>(null);
  const resolvedContainer =
    container !== undefined ? container : (parentPortal ?? undefined);

  React.useLayoutEffect(() => {
    const node = portalRef.current;
    setPortalNode((current) => (current === node ? current : node));
  });

  return (
    <DialogPortal ref={portalRef} container={resolvedContainer}>
      <DialogPortalContainerContext.Provider value={portalNode}>
        <DialogOverlay className={overlayClassName} />
        <DialogPrimitive.Popup
          data-slot="dialog-content"
          className={cn(
            'tw:fixed tw:top-1/2 tw:left-1/2 tw:z-50 tw:grid tw:w-full tw:max-w-[calc(100%-2rem)] tw:-translate-x-1/2 tw:-translate-y-1/2 tw:gap-4 tw:rounded-lg tw:border tw:border-border tw:bg-background tw:p-6 tw:text-foreground tw:shadow-lg tw:duration-100 tw:outline-none tw:sm:max-w-md tw:data-open:animate-in tw:data-open:fade-in-0 tw:data-open:zoom-in-95 tw:data-closed:animate-out tw:data-closed:fade-out-0 tw:data-closed:zoom-out-95',
            className
          )}
          {...props}
        >
          {children}
          {showCloseButton && (
            <DialogPrimitive.Close
              data-slot="dialog-close"
              render={
                <Button
                  type="button"
                  variant="ghost"
                  className="tw:absolute tw:top-4 tw:right-4"
                  size="icon-sm"
                />
              }
            >
              <XIcon />
              <span className="tw:sr-only">Close</span>
            </DialogPrimitive.Close>
          )}
        </DialogPrimitive.Popup>
      </DialogPortalContainerContext.Provider>
    </DialogPortal>
  );
}

function DialogHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="dialog-header"
      className={cn(
        'tw:flex tw:flex-col tw:items-start tw:gap-2 tw:text-left',
        className
      )}
      {...props}
    />
  );
}

function DialogFooter({
  className,
  showCloseButton = false,
  children,
  ...props
}: React.ComponentProps<'div'> & {
  showCloseButton?: boolean;
}) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        'tw:flex tw:flex-col-reverse tw:gap-2 tw:sm:flex-row tw:sm:justify-end',
        className
      )}
      {...props}
    >
      {children}
      {showCloseButton && (
        <DialogPrimitive.Close
          render={<Button type="button" variant="outline" />}
        >
          Close
        </DialogPrimitive.Close>
      )}
    </div>
  );
}

function DialogTitle({ className, ...props }: DialogPrimitive.Title.Props) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      render={<div role="heading" aria-level={2} />}
      className={cn(
        'tw:m-0 tw:text-lg tw:leading-7 tw:font-semibold tw:[font-family:Inter,sans-serif] tw:text-foreground',
        className
      )}
      {...props}
    />
  );
}

function DialogDescription({
  className,
  ...props
}: DialogPrimitive.Description.Props) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn(
        'tw:m-0! tw:text-sm tw:leading-5 tw:font-normal tw:[font-family:Inter,sans-serif] tw:text-balance tw:text-muted-foreground tw:md:text-pretty tw:*:[a]:underline tw:*:[a]:underline-offset-3 tw:*:[a]:hover:text-foreground',
        className
      )}
      {...props}
    />
  );
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
